import { useTheme } from '../../contexts/ThemeContext';
import { DocumentIcon, FolderIcon, PhotoIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

interface AttachmentDisplayProps {
  fileName: string;
  fileType?: string;
}

export function AttachmentDisplay({ fileName, fileType }: AttachmentDisplayProps) {
  const { theme } = useTheme();

  const isImage = fileType?.startsWith('image/') || false;
  const isPdf = fileType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf');
  const isZip = fileType === 'application/zip' || fileName?.toLowerCase().endsWith('.zip');

  const getFileType = (): string => {
    if (isImage) return 'IMAGE';
    if (isPdf) return 'PDF';
    if (isZip) return 'ZIP';
    if (fileName) {
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (ext) return ext.toUpperCase();
    }
    return 'FILE';
  };

  const fileTypeBadge = getFileType();
  const badgeClasses: Record<string, string> = {
    IMAGE: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    PDF: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    ZIP: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    DOC: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    XLS: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    CSV: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    FILE: 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-400'
  };

  const getIcon = () => {
    if (isImage) return <PhotoIcon className="h-5 w-5 text-purple-500" />;
    if (isPdf) return <DocumentTextIcon className="h-5 w-5 text-red-500" />;
    if (isZip) return <FolderIcon className="h-5 w-5 text-yellow-500" />;
    return <DocumentIcon className="h-5 w-5 text-blue-500" />;
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div
          className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
          }`}
        >
          {getIcon()}
        </div>
        <span
          className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
            badgeClasses[fileTypeBadge] || badgeClasses.FILE
          }`}
        >
          {fileTypeBadge}
        </span>
      </div>
        <details className="group w-full">
          <summary
            className={`text-xs cursor-pointer list-none ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
            } hover:underline`}
          >
            View file name
          </summary>
          <div
            className={`mt-1 p-2 rounded-md text-xs font-mono border break-all w-full ${
              theme === 'dark' ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700'
            }`}
          >
            {fileName}
          </div>
        </details>
    </div>
  );
}
